import React from "react";
import { Dayjs } from "dayjs";

import { DateRangeCriteria } from "../record_search/types";
import { RangeInput } from "./RangeInputs";
import { DatePickerPopover, RELATIVE_DATE_OPTIONS } from "./RelativeDatePicker";

export type Props = {
  values?: DateRangeCriteria;
  handleChange: (dates: DateRangeCriteria) => void;
  format?: string;
  disabled?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  showFutureDates?: boolean;
};

export default function DateRangePicker(props: Props): JSX.Element {
  const { values, handleChange, format, minDate, maxDate, showFutureDates = true } = props;

  const updateFrom = React.useCallback(
    (newVal?: Dayjs, newRelativeVal?: string) => {
      handleChange({ ...values, from: newVal, relativeFrom: newRelativeVal });
    },
    [handleChange, values],
  );

  const updateTo = React.useCallback(
    (newVal?: Dayjs, newRelativeVal?: string) => {
      handleChange({ ...values, to: newVal, relativeTo: newRelativeVal });
    },
    [handleChange, values],
  );

  // save having 2 separate callbacks with the same deps
  const disabledCallbacks = React.useMemo(
    () => ({
      from: (current?: Dayjs): boolean => {
        if (!current || !values) {
          return false;
        }
        if (minDate && current && minDate >= current) {
          return true;
        }
        if (maxDate && current && maxDate <= current) {
          return true;
        }
        if (values.relativeTo) {
          return RELATIVE_DATE_OPTIONS[values.relativeTo].value <= current;
        }
        return !!(values.to && values.to <= current);
      },
      to: (current?: Dayjs): boolean => {
        if (!current || !values) {
          return false;
        }
        if (minDate && current && minDate >= current) {
          return true;
        }
        if (maxDate && current && maxDate <= current) {
          return true;
        }
        if (values.relativeFrom) {
          return RELATIVE_DATE_OPTIONS[values.relativeFrom].value >= current;
        }
        return !!(values.from && values.from >= current);
      },
    }),
    [minDate, maxDate, values],
  );

  return (
    <RangeInput
      from={
        <DatePickerPopover
          value={values ? values.from : undefined}
          relativeValue={values?.relativeFrom}
          onSelect={updateFrom}
          format={format}
          disabledDate={disabledCallbacks.from}
          placeholder="Start"
          showFutureDates={showFutureDates}
          disabled={props.disabled}
        />
      }
      to={
        <DatePickerPopover
          value={values?.to}
          relativeValue={values?.relativeTo}
          onSelect={updateTo}
          format={format}
          disabledDate={disabledCallbacks.to}
          placeholder="End"
          showFutureDates={showFutureDates}
          disabled={props.disabled}
        />
      }
    />
  );
}
